/**
 * /results - the map. The centrepiece. Section 12.3.
 *
 * Three columns: layers on the left, the map and its timeline in the middle,
 * the run and a fixed cell readout on the right. Nothing floats over the map
 * except its own instruments - north arrow, scale, zoom, cursor position.
 *
 * On first open this selects the most recent completed run and opens it on
 * its widest-extent frame. The frame-loading policy lives in `useFrames`.
 */

import { memo, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react'
import { create } from 'zustand'

import { api, type GeoJsonFeatureCollection } from '../api/client'
import type { PointQuery, Run, Timesteps } from '../api/types'
import { useFrames } from '../api/useFrames'
import { AttenuationPanel } from '../components/AttenuationPanel'
import { ExportBar } from '../components/ExportBar'
import {
  type Bounds,
  type Isochrone,
  Playback,
  ResultsMap,
  type SettlementRisk,
  usePlaybackFrame,
} from '../components/FloodMap'
import { Hydrograph } from '../components/Hydrograph'
import { ISOCHRONE_MIN, LayerPanel, RASTER_NAMES, rampColour, useMapLayers } from '../components/LayerPanel'
import { ARRIVAL_RAMP, HAZARD, HAZARD_LABELS } from '../components/Legend'
import { isTimeVarying, useAppStore } from '../store/useAppStore'
import { C } from '../design/tokens'

const MONO = 'font-mono tabular-nums'
const LABEL = 'text-12 font-medium uppercase tracking-[0.06em] text-[var(--ink-text-dim)]'

/** The cell under the cursor, outside React so hovering never re-renders the page. */
const useHover = create<{ point: { lat: number; lon: number } | null; set: (p: { lat: number; lon: number } | null) => void }>(
  (set) => ({ point: null, set: (point) => point && set({ point }) }),
)

const pointKey = (runId: string, lat: number, lon: number) => ['point', runId, lat.toFixed(3), lon.toFixed(3)]

function bboxOf(fc: GeoJsonFeatureCollection | undefined): Bounds | null {
  if (!fc?.features?.length) return null
  let w = Infinity
  let s = Infinity
  let e = -Infinity
  let n = -Infinity
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      const [x, y] = c as number[]
      w = Math.min(w, x)
      e = Math.max(e, x)
      s = Math.min(s, y)
      n = Math.max(n, y)
    } else if (Array.isArray(c)) c.forEach(walk)
  }
  fc.features.forEach((f) => walk(f.geometry?.coordinates))
  return Number.isFinite(w) ? [w, s, e, n] : null
}

function nearestIndex(times: number[], t: number): number {
  let best = 0
  for (let i = 1; i < times.length; i += 1) if (Math.abs(times[i] - t) < Math.abs(times[best] - t)) best = i
  return best
}

function clock(seconds: number): string {
  const minutes = Math.round(seconds / 60)
  return `T + ${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

/**
 * The nine hazard thresholds, read from the provenance page's own row so the
 * readout classifies with exactly the numbers that page shows.
 */
interface Thresholds {
  dv: number[]
  d: number[]
  v: number[]
}
function parseThresholds(value: unknown): Thresholds | null {
  if (typeof value !== 'string') return null
  const grab = (key: string) => {
    const m = value.match(new RegExp(`(?:^|[\\s,])${key}\\s+([\\d.]+)/([\\d.]+)/([\\d.]+)`))
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null
  }
  const dv = grab('dv')
  const d = grab('d')
  const v = grab('v')
  return dv && d && v ? { dv, d, v } : null
}

/** backend/analysis/hazard.classify, applied to one cell at one instant. */
function classAt(depth: number, velocity: number, t: Thresholds): number {
  if (!(depth > 0)) return 0
  let cls = 1
  for (let i = 0; i < 3; i += 1) {
    if (depth * velocity >= t.dv[i] || depth >= t.d[i] || velocity >= t.v[i]) cls = i + 2
  }
  return cls
}

// --- timeline -----------------------------------------------------------------

const SPEEDS = [0.5, 1, 2, 4]

function Timeline({ playback, timesteps }: { playback: Playback; timesteps: Timesteps }) {
  const playing = useSyncExternalStore(playback.subscribe, () => playback.playing)
  const speed = useSyncExternalStore(playback.subscribe, () => playback.speed)
  const clockRef = useRef<HTMLSpanElement | null>(null)
  const frameRef = useRef<HTMLSpanElement | null>(null)
  const fillRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const times = timesteps.times_s
  const last = Math.max(1, timesteps.count - 1)

  useEffect(() => {
    const render = () => {
      const lo = Math.floor(playback.pos)
      const hi = Math.min(lo + 1, timesteps.count - 1)
      const t = times[lo] + ((times[hi] ?? times[lo]) - times[lo]) * (playback.pos - lo)
      if (clockRef.current) clockRef.current.textContent = clock(t)
      if (frameRef.current) frameRef.current.textContent = `frame ${Math.round(playback.pos)} / ${last}`
      if (fillRef.current) fillRef.current.style.width = `${(playback.pos / last) * 100}%`
      if (inputRef.current && document.activeElement !== inputRef.current) inputRef.current.value = String(playback.pos)
    }
    render()
    return playback.subscribe(render)
  }, [playback, timesteps, times, last])

  // The wet area through the run, from the stored depth via /timesteps.
  const profile = useMemo(() => {
    const counts = timesteps.wet_counts ?? []
    const peak = Math.max(0, ...counts)
    if (!peak) return null
    const pts = counts.map((c, i) => `${i},${(1 - c / peak).toFixed(4)}`)
    const widest = counts.indexOf(peak)
    return { d: `M0,1 L${pts.join(' L')} L${counts.length - 1},1 Z`, widest }
  }, [timesteps])

  const button =
    'flex h-9 w-9 items-center justify-center rounded-[4px] border border-[var(--ink-border)] bg-[var(--ink-surface)] text-[var(--ink-text)] transition-colors duration-150 hover:border-[var(--ink-accent-dim)] disabled:text-[var(--ink-text-faint)]'

  return (
    <div className="flex h-[84px] shrink-0 items-center gap-3 border-t border-[var(--ink-border)] bg-[var(--ink-surface)] px-4">
      <style>{`
        .flood-scrub { -webkit-appearance: none; appearance: none; background: transparent; cursor: pointer; margin: 0; }
        .flood-scrub::-webkit-slider-runnable-track { height: 18px; background: transparent; }
        .flood-scrub::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; margin-top: 2px; border-radius: 50%; background: var(--ink-text); border: 2px solid var(--ink-accent); }
        .flood-scrub::-moz-range-track { height: 18px; background: transparent; }
        .flood-scrub::-moz-range-thumb { width: 10px; height: 10px; border-radius: 50%; background: var(--ink-text); border: 2px solid var(--ink-accent); }
        .flood-scrub:focus-visible { outline: none; }
        .flood-scrub:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 2px var(--ink-bg), 0 0 0 4px var(--ink-accent); }
      `}</style>

      <div className="flex shrink-0 flex-col items-center gap-1.5">
        <div className="flex items-center gap-1.5">
          <button type="button" className={button} onClick={() => playback.step(-1)} aria-label="Previous frame">
            <SkipBack className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`${button} ${playing ? '' : 'border-[var(--ink-accent-dim)]'}`}
            onClick={() => playback.toggle()}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 text-[var(--ink-accent)]" />}
          </button>
          <button type="button" className={button} onClick={() => playback.step(1)} aria-label="Next frame">
            <SkipForward className="h-4 w-4" />
          </button>
        </div>

        <div className="flex overflow-hidden rounded-[4px] border border-[var(--ink-border)]" role="group" aria-label="Playback speed">
          {SPEEDS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => playback.setSpeed(value)}
              className={`h-6 w-[29px] border-l border-[var(--ink-border)] text-12 first:border-l-0 ${MONO} transition-colors duration-150 ${
                speed === value ? 'bg-[var(--ink-surface-raised)] text-[var(--ink-accent)]' : 'text-[var(--ink-text-dim)] hover:text-[var(--ink-text)]'
              }`}
              aria-pressed={speed === value}
            >
              {value}×
            </button>
          ))}
        </div>
      </div>

      <div className="w-[152px] shrink-0">
        <span ref={clockRef} className={`block text-32 font-semibold leading-none text-[var(--ink-text)] ${MONO}`} />
        <span className={`mt-1 block text-12 leading-tight text-[var(--ink-text-dim)] ${MONO}`}>of {clock(times[timesteps.count - 1] ?? 0)}</span>
        <span ref={frameRef} className={`block text-12 leading-tight text-[var(--ink-text-dim)] ${MONO}`} />
      </div>

      <div className="relative h-[52px] min-w-0 flex-1">
        <div className="absolute inset-x-[7px] top-0 h-[30px]">
          {profile && (
            <svg className="h-full w-full" viewBox={`0 0 ${last} 1`} preserveAspectRatio="none" aria-label="Wet area through the run">
              <path d={profile.d} fill={C.border} />
              <line x1={profile.widest} x2={profile.widest} y1={0} y2={1} stroke={C.textDim} strokeWidth={1} vectorEffect="non-scaling-stroke" />
            </svg>
          )}
        </div>
        <div className="absolute inset-x-[7px] top-[37px] h-[3px] rounded-[2px] bg-[var(--ink-border)]">
          <div ref={fillRef} className="h-full rounded-[2px] bg-[var(--ink-accent)]" />
        </div>
        <input
          ref={inputRef}
          type="range"
          min={0}
          max={last}
          step={0.01}
          defaultValue={0}
          onInput={(event) => {
            playback.pause()
            playback.seek(Number(event.currentTarget.value))
          }}
          onPointerUp={() => playback.snap()}
          onKeyUp={() => playback.snap()}
          className="flood-scrub absolute inset-x-0 top-[29px] h-[18px] w-full"
          aria-label="Simulation time"
        />
        <div className={`absolute inset-x-[7px] bottom-0 flex justify-between text-12 leading-none text-[var(--ink-text-dim)] ${MONO}`}>
          <span>0 h</span>
          <span>{Math.round((times[timesteps.count - 1] ?? 0) / 3600)} h</span>
        </div>
      </div>
    </div>
  )
}

// --- readout ------------------------------------------------------------------

function ReadoutRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-[var(--ink-border)] py-2 first:border-t-0">
      <dt className="text-14 text-[var(--ink-text-dim)]">{label}</dt>
      <dd className={`text-right text-20 leading-tight text-[var(--ink-text)] ${MONO}`}>{children}</dd>
    </div>
  )
}

function HazardValue({ cls }: { cls: number }) {
  if (cls === 0) return <span className="text-14 text-[var(--ink-text-dim)]">Dry</span>
  return (
    <span className="inline-flex items-center gap-2 text-14">
      <span className="h-3 w-3 rounded-[2px]" style={{ background: HAZARD[cls] }} />
      {HAZARD_LABELS[cls]}
    </span>
  )
}

function Readout({
  runId,
  playback,
  timesteps,
  thresholds,
}: {
  runId: string
  playback: Playback
  timesteps: Timesteps | undefined
  thresholds: Thresholds | null
}) {
  const hovered = useHover((s) => s.point)
  const frame = usePlaybackFrame(playback)
  const query = useQuery({
    queryKey: hovered ? pointKey(runId, hovered.lat, hovered.lon) : ['point', runId, 'none'],
    queryFn: () => api.point(runId, Number(hovered!.lat.toFixed(3)), Number(hovered!.lon.toFixed(3))),
    enabled: hovered !== null,
    staleTime: Infinity,
    placeholderData: (previous) => previous,
  })
  const data: PointQuery | undefined = query.data

  const k = data && timesteps ? (data.times_s.length === timesteps.count ? frame : nearestIndex(data.times_s, timesteps.times_s[frame])) : null
  const depth = k !== null ? data!.depth_series_m[k] : null
  const velocity = k !== null ? data!.velocity_series_ms[k] : null

  return (
    <section className="rounded-[6px] border border-[var(--ink-border)] bg-[var(--ink-surface)] p-4">
      <div className="flex items-baseline justify-between">
        <h3 className={LABEL}>Cell readout</h3>
        {timesteps && <span className={`text-14 text-[var(--ink-text)] ${MONO}`}>{timesteps.times_label[frame]}</span>}
      </div>

      {!data && !query.isFetching && <p className="mt-3 text-14 text-[var(--ink-text-dim)]">Point at the map to read a cell.</p>}
      {!data && query.isFetching && (
        <div className="mt-3 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-6" />
          ))}
        </div>
      )}

      {data && (
        <div className={`transition-opacity duration-150 ${query.isPlaceholderData ? 'opacity-60' : ''}`}>
          <p className={`mt-1 text-14 text-[var(--ink-text-dim)] ${MONO}`}>
            {data.lat.toFixed(4)}° N · {data.lon.toFixed(4)}° E
          </p>
          <dl className="mt-2">
            {depth !== null && <ReadoutRow label="Depth">{depth.toFixed(2)} m</ReadoutRow>}
            {velocity !== null && <ReadoutRow label="Velocity">{velocity.toFixed(2)} m/s</ReadoutRow>}
            <ReadoutRow label="Arrival">
              {data.arrival_time_min === null ? (
                <span className="text-14 text-[var(--ink-text-dim)]">Not reached</span>
              ) : (
                clock(data.arrival_time_min * 60)
              )}
            </ReadoutRow>
            {thresholds && depth !== null && velocity !== null && (
              <ReadoutRow label="Hazard now">
                <HazardValue cls={classAt(depth, velocity, thresholds)} />
              </ReadoutRow>
            )}
            <ReadoutRow label="Hazard, run peak">
              <HazardValue cls={data.hazard_class ?? 0} />
            </ReadoutRow>
            <ReadoutRow label="Chainage">{data.chainage_km.toFixed(1)} km</ReadoutRow>
            <ReadoutRow label="Ground">{data.elevation_m.toFixed(0)} m</ReadoutRow>
          </dl>
        </div>
      )}
    </section>
  )
}

// --- right column -----------------------------------------------------------------

function Figure({ label, value, unit, note }: { label: string; value: string; unit: string; note?: string }) {
  return (
    <div className="metric-tile">
      <div className="section-label">{label}</div>
      <div className="whitespace-nowrap">
        <span className="key-figure">{value}</span>
        <span className="unit">{unit}</span>
      </div>
      {note && <div className="metric-note">{note}</div>}
    </div>
  )
}

/**
 * The four headline figures, above the map. Depth, velocity and area come from
 * the run summary; the arrival is the earliest of the settlement cells this run
 * actually wetted, and names the settlement it belongs to.
 */
function Headline({ runId, settlements }: { runId: string; settlements: SettlementRisk[] }) {
  const summary = useQuery({ queryKey: ['summary', runId], queryFn: () => api.summary(runId) })
  const s = summary.data

  const earliest = useMemo(() => {
    let best: { minutes: number; name: string } | null = null
    for (const { settlement, point } of settlements) {
      const m = point?.arrival_time_min
      if (m === null || m === undefined) continue
      if (!best || m < best.minutes) best = { minutes: m, name: settlement.name }
    }
    return best
  }, [settlements])

  if (!s) {
    return (
      <div className="grid shrink-0 grid-cols-4 gap-3 px-4 pb-3 pt-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton h-[92px]" />
        ))}
      </div>
    )
  }

  return (
    <div className="grid shrink-0 grid-cols-4 gap-3 px-4 pb-3 pt-4">
      <Figure label="Max depth" value={s.max_depth_m != null ? s.max_depth_m.toFixed(2) : '—'} unit="m" />
      <Figure label="Max velocity" value={s.max_velocity_ms != null ? s.max_velocity_ms.toFixed(2) : '—'} unit="m/s" />
      <Figure label="Flooded area" value={s.flooded_area_km2 != null ? s.flooded_area_km2.toFixed(1) : '—'} unit="km²" />
      <Figure
        label="Earliest arrival"
        value={earliest ? clock(earliest.minutes * 60).replace('T + ', '') : '—'}
        unit="h:mm"
        note={earliest ? `at ${earliest.name}` : undefined}
      />
    </div>
  )
}

const RunPicker = memo(function RunPicker({ runId, run, selectable }: { runId: string; run: Run | undefined; selectable: Run[] }) {
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  return (
    <div>
      <label className={LABEL} htmlFor="run-select">
        Run
      </label>
      <select
        id="run-select"
        value={runId}
        onChange={(event) => setSelectedRunId(event.target.value)}
        className="mt-1 w-full rounded-[4px] border border-[var(--ink-border)] bg-[var(--ink-surface-raised)] px-2 py-2 text-14 text-[var(--ink-text)]"
      >
        {selectable.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.id} · {candidate.solver} · {candidate.grid_resolution_m} m
            {candidate.status === 'done' ? '' : ` · ${candidate.status}`}
          </option>
        ))}
      </select>
      {run?.grid_resolution_m != null && run.wall_clock_s != null && (
        <p className={`mt-1 text-14 text-[var(--ink-text-dim)] ${MONO}`}>
          {run.grid_resolution_m} m grid · solved in {(run.wall_clock_s / 60).toFixed(1)} min
        </p>
      )}
    </div>
  )
})

const RunDetails = memo(function RunDetails({ runId }: { runId: string }) {
  const summary = useQuery({ queryKey: ['summary', runId], queryFn: () => api.summary(runId) })
  const s = summary.data
  return (
    <>
      {summary.isPending && (
        <div className="grid gap-2">
          {[0, 1].map((i) => (
            <div key={i} className="skeleton h-[92px]" />
          ))}
        </div>
      )}
      {s && (
        <div className="grid gap-2">
          {s.peak_discharge_cumecs != null && s.peak_discharge_cumecs > 0 && (
            <Figure label="Peak discharge" value={Math.round(s.peak_discharge_cumecs).toLocaleString('en-IN')} unit="m³/s" />
          )}
          {s.total_volume_m3 != null && s.total_volume_m3 > 0 && (
            <Figure label="Volume" value={(s.total_volume_m3 / 1e9).toFixed(2)} unit="km³" />
          )}
        </div>
      )}
      <div>
        <p className={LABEL}>Export</p>
        <div className="mt-1.5">
          <ExportBar runId={runId} />
        </div>
      </div>
      <AttenuationPanel runId={runId} />
      <Hydrograph runId={runId} />
    </>
  )
})

// --- page -------------------------------------------------------------------------

export default function Results() {
  const selectedRunId = useAppStore((s) => s.selectedRunId)
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const activeLayer = useAppStore((s) => s.activeLayer)
  const showIsochrones = useMapLayers((s) => s.visible.isochrones)
  const compare = useMapLayers((s) => s.compare)
  const compareLayer = useMapLayers((s) => s.compareLayer)

  const runs = useQuery({ queryKey: ['runs'], queryFn: api.runs })
  const studyArea = useQuery({ queryKey: ['study-area'], queryFn: api.studyArea })
  const settlements = useQuery({ queryKey: ['settlements'], queryFn: api.settlements })
  const provenance = useQuery({ queryKey: ['provenance'], queryFn: api.provenance, staleTime: Infinity })

  // THE PAGE HAS ONE BASEMAP, so it offers one site's runs. /runs now says which
  // site each run is of, and a run of any other site drawn over this terrain is
  // a black frame - which is what opening on the newest run overall produced,
  // that being a Sikkim GLOF run while the basemap is Ujjani's.
  //
  // App.tsx wraps this page in RunScoped, so it only ever mounts with the site
  // every 2-D run belongs to already active; a site with no runs gets that
  // component's notice instead of this map.
  const siteRuns = useMemo(
    () => runs.data?.filter((run) => run.site_id === activeSiteId) ?? [],
    [runs.data, activeSiteId],
  )
  // `/runs` is ordered by when runs actually happened, so the first done is newest.
  const completed = useMemo(() => siteRuns.filter((run) => run.status === 'done'), [siteRuns])
  // Failed and cancelled runs stay selectable: a run that died server-side must
  // appear, not leave the previous one silently on screen.
  const selectable = useMemo(
    () => siteRuns.filter((run) => ['done', 'failed', 'cancelled'].includes(run.status)),
    [siteRuns],
  )

  // A run left selected from elsewhere may belong to another site. Dropping it
  // lets the choice below open this site's newest completed run instead.
  useEffect(() => {
    const current = runs.data?.find((run) => run.id === selectedRunId)
    if (current && current.site_id !== activeSiteId) setSelectedRunId(null)
  }, [activeSiteId, selectedRunId, runs.data, setSelectedRunId])

  useEffect(() => {
    if (selectedRunId === null && completed.length > 0) setSelectedRunId(completed[0].id)
  }, [selectedRunId, completed, setSelectedRunId])

  const runId = selectedRunId
  const run = runs.data?.find((candidate) => candidate.id === runId)
  const done = run?.status === 'done'

  const timesteps = useQuery({
    queryKey: ['timesteps', runId],
    queryFn: () => api.timesteps(runId as string),
    enabled: Boolean(runId) && done,
  })
  const reach = useQuery({
    queryKey: ['reach', studyArea.data?.dam?.id],
    queryFn: () => api.reach(studyArea.data!.dam!.id),
    enabled: Boolean(studyArea.data?.dam?.id),
  })
  const extent = useQuery({
    queryKey: ['extent', runId],
    queryFn: () => api.extent(runId as string),
    enabled: Boolean(runId) && done,
  })

  // --- playback: one clock per page, opened on the widest-extent frame ----------
  const [playback] = useState(() => new Playback())
  useEffect(() => () => playback.dispose(), [playback])
  const openedFor = useRef<string | null>(null)
  useEffect(() => {
    const ts = timesteps.data
    if (!ts || !runId) return
    playback.setLast(ts.count - 1)
    if (openedFor.current === runId) return
    openedFor.current = runId
    const counts = ts.wet_counts ?? []
    const widest = counts.length ? counts.indexOf(Math.max(...counts)) : -1
    playback.pause()
    playback.seek(widest > 0 ? widest : (ts.first_wet_frame ?? 0))
  }, [timesteps.data, runId, playback])
  // The store's frame follows the clock whenever it rests on a stored frame.
  useEffect(
    () =>
      playback.subscribe(() => {
        if (playback.playing) return
        const k = Math.round(playback.pos)
        if (useAppStore.getState().frameIndex !== k) useAppStore.getState().setFrameIndex(k)
      }),
    [playback],
  )

  // Space plays and pauses; the arrow keys step one stored frame.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(target.tagName)) return
      if (event.key === ' ') playback.toggle()
      else if (event.key === 'ArrowLeft') playback.step(-1)
      else if (event.key === 'ArrowRight') playback.step(1)
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [playback])

  // --- rasters -------------------------------------------------------------------
  const animated = isTimeVarying(activeLayer)
  const frames = useFrames(animated && done ? runId : null, activeLayer, animated ? timesteps.data : undefined)
  const staticOverlay = useQuery({
    queryKey: ['overlay', runId, activeLayer],
    queryFn: () => api.overlay(runId as string, activeLayer),
    enabled: Boolean(runId) && done && !animated,
  })
  const compareOverlay = useQuery({
    queryKey: ['overlay', runId, compareLayer],
    queryFn: () => api.overlay(runId as string, compareLayer),
    enabled: Boolean(runId) && done && compare,
  })

  // --- vectors -------------------------------------------------------------------
  const reachCollection: GeoJsonFeatureCollection | null = useMemo(() => {
    if (!reach.data?.geometry_geojson) return null
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: JSON.parse(reach.data.geometry_geojson), properties: { name: reach.data.name } }],
    }
  }, [reach.data])

  const isoTimes = useMemo(() => {
    const ts = timesteps.data
    if (!ts?.count) return []
    const end = ts.times_s[ts.count - 1]
    return ISOCHRONE_MIN.filter((m) => m * 60 <= end).map((m) => {
      const frame = nearestIndex(ts.times_s, m * 60)
      return { frame, minutes: ts.times_s[frame] / 60, label: ts.times_label[frame] }
    })
  }, [timesteps.data])
  const isoQueries = useQueries({
    queries: isoTimes.map((it) => ({
      queryKey: ['extent', runId, it.frame],
      queryFn: () => api.extent(runId as string, it.frame),
      enabled: Boolean(runId) && done && showIsochrones,
      staleTime: Infinity,
    })),
  })
  const isoStamp = isoQueries.map((q) => q.dataUpdatedAt).join(',')
  const isochrones: Isochrone[] = useMemo(
    () =>
      isoTimes.flatMap((it, i) => {
        const data = isoQueries[i]?.data
        return data?.features.length ? [{ minutes: it.minutes, label: it.label, colour: rampColour(ARRIVAL_RAMP, it.minutes), data }] : []
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isoTimes, isoStamp],
  )

  // /point clamps to the grid edge, so a settlement outside this run's grid is
  // never queried - it would read the edge cell's water as its own.
  const gridBounds = frames.bounds ?? staticOverlay.data?.bounds ?? null
  const inGrid = (lat: number, lon: number) =>
    gridBounds !== null && lon >= gridBounds[0] && lon <= gridBounds[2] && lat >= gridBounds[1] && lat <= gridBounds[3]
  const riskQueries = useQueries({
    queries: (settlements.data ?? []).map((settlement) => ({
      queryKey: pointKey(runId ?? '', settlement.lat, settlement.lon),
      queryFn: () => api.point(runId as string, Number(settlement.lat.toFixed(3)), Number(settlement.lon.toFixed(3))),
      enabled: Boolean(runId) && done && inGrid(settlement.lat, settlement.lon),
      staleTime: Infinity,
    })),
  })
  const riskStamp = riskQueries.map((q) => q.dataUpdatedAt).join(',')
  const gridKey = gridBounds?.join(',') ?? ''
  const settlementRisk: SettlementRisk[] = useMemo(
    () =>
      (settlements.data ?? []).map((settlement, i) => ({
        settlement,
        point: inGrid(settlement.lat, settlement.lon) ? (riskQueries[i]?.data ?? null) : null,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settlements.data, riskStamp, gridKey],
  )

  const thresholds = useMemo(
    () => parseThresholds(provenance.data?.rows.find((row) => row.parameter === 'Hazard thresholds')?.value),
    [provenance.data],
  )

  // --- framing: fixed when the map first mounts -------------------------------
  const extentBounds = useMemo(() => bboxOf(extent.data as GeoJsonFeatureCollection | undefined), [extent.data])
  const latestHome = extentBounds ?? (extent.isFetched || extent.isError ? (studyArea.data?.bbox_wgs84 ?? null) : null)
  const [firstHome, setFirstHome] = useState<Bounds | null>(null)
  useEffect(() => {
    if (!firstHome && latestHome) setFirstHome(latestHome)
  }, [firstHome, latestHome])

  const durationMin = timesteps.data?.count ? timesteps.data.times_s[timesteps.data.count - 1] / 60 : null

  // --- states that are not a map ------------------------------------------------
  if (runs.isPending) return <div className="skeleton m-6 h-[calc(100%-48px)]" />

  if (runs.isError) {
    return (
      <div className="p-8">
        <h2 className="text-20 font-semibold text-[var(--ink-text)]">Cannot reach the API</h2>
        <p className="mt-2 text-14 text-[var(--ink-text-dim)]">{runs.error instanceof Error ? runs.error.message : String(runs.error)}</p>
        <p className="mt-3 text-14 text-[var(--ink-text-dim)]">
          Start it from the repository root with <code className="font-mono text-[var(--ink-text)]">uvicorn backend.app.main:app --reload</code>
        </p>
      </div>
    )
  }

  if (completed.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-lg">
          <h2 className="text-20 font-semibold text-[var(--ink-text)]">No completed run</h2>
          <p className="mt-2 text-14 text-[var(--ink-text-dim)]">Solve a scenario from Scenario and Run, or from the command line:</p>
          <code className="mt-3 block rounded-[4px] border border-[var(--ink-border)] bg-[var(--ink-surface)] p-3 font-mono text-14 text-[var(--ink-text)]">
            python -m backend.pipeline.cli run --scenario config/demo_scenario.yaml --out data/runs/demo
          </code>
        </div>
      </div>
    )
  }

  if (run && !done) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          <h2 className={`text-20 font-semibold ${run.status === 'failed' ? 'text-[var(--ink-alert)]' : 'text-[var(--ink-warn)]'}`}>
            {run.status === 'failed' ? 'This run failed' : `This run was ${run.status}`}
          </h2>
          <p className={`mt-1 text-14 text-[var(--ink-text-dim)] ${MONO}`}>
            {run.id} · {run.solver}
          </p>
          {run.error_message && (
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-[4px] border border-[var(--ink-alert-dim)] bg-[var(--ink-surface)] p-3 font-mono text-14 leading-snug text-[var(--ink-alert-text)]">
              {run.error_message}
            </pre>
          )}
          {run.progress_message && (
            <p className={`mt-2 text-14 text-[var(--ink-text-dim)] ${MONO}`}>
              Last progress: {run.progress_message} ({run.progress_pct.toFixed(0)}%)
            </p>
          )}
          {selectable.length > 1 && (
            <div className="mt-4">
              <RunPicker runId={run.id} run={run} selectable={selectable} />
            </div>
          )}
        </div>
      </div>
    )
  }

  // The map frames the run's own grid, as the API returns it; the flood extent and
  // the study area only stand in until the grid bounds arrive.
  const home: Bounds | null = gridBounds ?? latestHome ?? firstHome

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-[var(--ink-bg)] text-[var(--ink-text)]" data-testid="inundation-page">
      {runId && <Headline runId={runId} settlements={settlementRisk} />}
      <div className="flex w-full" style={{ height: 'max(70vh, 560px)' }}>
        <aside
          className="w-[272px] shrink-0 overflow-y-auto border-r border-[var(--ink-border)] bg-[var(--ink-surface)] 2xl:w-[300px]"
          aria-label="Map layers"
          data-testid="layer-panel"
        >
          <LayerPanel durationMin={durationMin} />
        </aside>
      <div className="relative min-w-0 flex-1" data-testid="inundation-map">
        {home ? (
          <ResultsMap
            playback={playback}
            frames={frames}
            animated={animated}
            staticOverlay={staticOverlay.data ?? null}
            compareOverlay={compare ? (compareOverlay.data ?? null) : null}
            homeBounds={home}
            runId={runId}
            gridBounds={gridBounds}
            reach={reachCollection}
            extent={(extent.data as GeoJsonFeatureCollection | undefined) ?? null}
            isochrones={isochrones}
            settlements={settlementRisk}
            dam={studyArea.data?.dam ?? null}
            onHover={useHover.getState().set}
          />
        ) : (
          <div className="skeleton absolute inset-0 rounded-none" />
        )}
      </div>
      </div>
      <div className="grid gap-3 border-t border-[var(--ink-border)] p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" aria-label="Run and readout" data-testid="map-readouts">
        {runId && <RunPicker runId={runId} run={run} selectable={selectable} />}
        {runId && <Readout runId={runId} playback={playback} timesteps={timesteps.data} thresholds={thresholds} />}
        {compare && (
          <div className="text-12 text-[var(--ink-text-dim)] lg:col-span-2" data-testid="compare-sides">
            Swipe compare · left of the divider: {RASTER_NAMES[activeLayer]} · right: {RASTER_NAMES[compareLayer]}
          </div>
        )}
      </div>
      {timesteps.data ? (
        <Timeline playback={playback} timesteps={timesteps.data} />
      ) : (
        <div className="h-[84px] shrink-0 border-t border-[var(--ink-border)] bg-[var(--ink-surface)] p-4">
          <div className="skeleton h-full" />
        </div>
      )}
      {runId && (
        <div className="p-4">
          <RunDetails runId={runId} />
        </div>
      )}
    </div>
  )
}
